<script setup lang="ts">
import type { GradientType, Level, RenderAs } from "qrcode.vue";
import QrcodeVue from "qrcode.vue";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";

// interface
interface Prop {
  isVisible: boolean;
  qrValue: string;
  booking?: any;
}

interface Emit {
  (e: "closeDialog", val: boolean): void;
}

// Props
const props = defineProps<Prop>();

// Emit
const emit = defineEmits<Emit>();

// data
const level = ref<Level>("M");
const renderAs = ref<RenderAs>("svg");
const background = ref("#ffffff");
const foreground = ref("#000000");
const margin = ref(2);
const gradient = ref(false);
const gradientType = ref<GradientType>("linear");
const gradientStartColor = ref("#000000");
const gradientEndColor = ref("#38bdf8");
const finalLocks:any = ref([])
const watchPosId:any = ref(null)
const locationErrorMsq = ref('')
const getLocation = () => {
  if (navigator.geolocation) {
    watchPosId.value = navigator.geolocation.watchPosition(LocationSuccess, LocationError);
  } else {
    locationErrorMsq.value = 'Must be at venue to open doors on Device, if issues please use pin code on reader'
  }
}

onUnmounted(()=>{
  if (navigator.geolocation) {
    navigator.geolocation.clearWatch(watchPosId.value);
  }
  isAfterLock.value = {}
})

const LocationSuccess = (position:any) => {
  let lockOpen = false
  finalLocks.value.forEach((lock:any) => {
    let kms = distanceInKmBetweenEarthCoordinates(lock?.geoLocation?.lat, lock?.geoLocation?.lon, position.coords.latitude,position.coords.longitude)
    let meters = Math.floor(kms * 1000)
    if(meters <= lock.geoMaxDistance){
      lock.enabled = true
    } else {
      lock.enabled = false
    }
  })
  if(!lockOpen){
    locationErrorMsq.value = 'Must be at venue to open doors on Device, if issues please use pin code on reader'
  }

  if(isAfterLock?.value?.id){
    locationErrorMsq.value = '';
    openDoor(isAfterLock.value)
    isAfterLock.value = {}
  }
}

const degreesToRadians = (degrees:number) => {
  return degrees * Math.PI / 180;
}

const distanceInKmBetweenEarthCoordinates = (lat1:number, lon1:number, lat2:number, lon2:number) => {
  let earthRadiusKm = 6371;

  let dLat = degreesToRadians(lat2-lat1);
  let dLon = degreesToRadians(lon2-lon1);

  lat1 = degreesToRadians(lat1);
  lat2 = degreesToRadians(lat2);

  let a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
  let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return earthRadiusKm * c;
}


const LocationError = (error:any) => {
  locationErrorMsq.value = 'Must be at venue to open doors on Device, if issues please use pin code on reader'
  isAfterLock.value = {}
  lockLoading.value = false
}

const lockLoading = ref(false)
const isAfterLock:any = ref({})
const openDoor = async (lock:any) => {
  lockLoading.value = true
  if(!watchPosId.value && lock?.id) {
    isAfterLock.value = lock
    getLocation()
    lockLoading.value = false
    return
  } else if(lock?.id && lock?.enabled) {
    let lockState = await pb.collection("doors").update(lock.id, {activate: true})
    lock.state = "Open"
    lock.activate = lockState.activate
    if (lockState?.onSeconds) {
      window.setTimeout(async () => {
        let tmpLock = await pb.collection("doors").getOne(lock.id)
        tmpLock.enabled = lock.enabled
        lock.state = tmpLock.state
        lock = tmpLock
        lockLoading.value = false
        isAfterLock.value = {}
      }, (lockState.onSeconds + 1) * 1000)
    }
  } else {
    lockLoading.value = false
  }
}

onMounted(async ()=>{
  // Get users Permissions
  isAfterLock.value = {}
  finalLocks.value = [];
  try {
    if ((pb?.authStore?.model?.membership && pb?.authStore?.model?.membership != '') || (pb?.authStore?.model?.roles && pb?.authStore?.model?.roles.length > 0)) {

      let roles = pb?.authStore?.model?.roles && pb?.authStore?.model?.roles.length > 0 ? pb?.authStore?.model?.roles.map(role => 'roles~"' + role + '"').join("||") : ''
      if (roles != '') {
        roles = '|| ' + roles
      }

      // Get Activity Location
      let location = pb?.authStore?.model?.homeLocation
      try{
        let locationActivityCheck = await pb.collection("activities").getOne(props?.booking?.activity)
        if(locationActivityCheck){
          location = locationActivityCheck.location
        }
      }catch(e){

      }

      let locks = await pb.collection("doors").getFullList(
        {filter: 'enabled=true && location="' + location + '" && enablePhoneGeoAccess = true && ' +
            'bookingActivityAccess~"' + (props?.booking?.activity || '') + '"' +
            '&& (membershipaccess~"' + pb?.authStore?.model?.membership + '" || allMemberships=true' + roles + ')'})

      // remove Locks
      if (locks) {
        let bookingTime = moment(props?.booking.startDate)
        let bookingTimeEnd = moment(bookingTime).add(props?.booking.duration, 'minutes');
        let now = moment()
        locks.forEach(lock => {
          lock.enabled = false
          if(lock?.beforeBookingMins > 0){
            let openTime = moment(bookingTime).add((lock?.beforeBookingMins || 0) * -1,"minutes")
            let closeTime = moment(bookingTimeEnd).add(lock?.afterBookingMins || 0, "minutes")
            if(now.isBetween(openTime, closeTime)){
              finalLocks.value.push(lock)
            } else {
              locationErrorMsq.value = "Must be within booking window to open door"
            }
          } else {
            finalLocks.value.push(lock)
          }
        })
      }
    }
  }catch(e){
  }
})
</script>

<template>
  <VDialog
    :model-value="props?.isVisible"
    persistent
    class="d-flex justify-center align-center qr-dialog pair_terminal_dialog"
  >
    <VCard
      height="auto"
      width="400"
      style="max-height:90vh;max-width:95vw"
      class="rounded-lg shadow-md bg-gradient-to-br from-blue-50 to-blue-100 overflow-auto">
      <div class="d-flex align-center justify-space-between bg-dialogHeaderColor">
        <VCardTitle>Scan for Access</VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="emit('closeDialog', false)"
        ></VBtn>
      </div>
      <div class="px-6 py-4 text-center">
        <qrcode-vue
          v-if="qrValue && qrValue != ''"
          text="testing"
          :value="`BT#${qrValue}`"
          :level="level"
          :render-as="renderAs"
          :background="background"
          :foreground="foreground"
          :gradient="gradient"
          :gradient-type="gradientType"
          :gradient-start-color="gradientStartColor"
          :gradient-end-color="gradientEndColor"
          :margin="margin"
          size="320"
          class="qr-code_container"
          style="max-width:100%"
        />
        <div class="mt-4"></div>
        <h3>Entry Code: {{qrValue}}</h3>
        <p class="font-weight-medium">
          valid for single booking
        </p>
        <v-alert color="error" variant="tonal" class="mb-3" v-if="locationErrorMsq != ''">
          {{locationErrorMsq}}
        </v-alert>
        <div v-for="lock in finalLocks" class="mb-2">
          <v-btn class="w-100" @click.prevent="openDoor(lock)" :loading="lockLoading" :color="lock.state=='Open' ? 'success' : 'primary'">Open {{lock.name}}</v-btn>
        </div>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped>
.qr-dialog {
  backdrop-filter: blur(6px);
}

.shadow-md {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 10%);
}

.border-b {
  border-block-end: 1px solid #e5e7eb;
}

.rounded-lg {
  border-radius: 12px;
}

.qr-code_container {
  inline-size: fill-available;
}
</style>
